/*#
# Remote: the service-compris client

The thin fetch wrapper virta talks to a [service-compris](https://github.com/tonioloewald/tosijs-platform)
host through, and the [sync log](/log/) built on it. `service-compris` the
package is the platform's decision kernel only — no HTTP, no client — so this
is the whole client, and it is small on purpose: the universal endpoint is
four routes.

```
GET    /doc?p=<collection>/<id>          → the document (404 → null)
PUT    /doc   { p, data }                → "updated …" | "unchanged …"   (update only)
POST   /doc   { p, data }                → "created …"                    (create only)
DELETE /doc?p=…
GET    /docs?p=<collection>&c=&o=&f=     → [ { …fields, _path } ]
GET    /docs?p=<collection>&since=<seq>  → { rows, cursor, more }        (a sequenced collection)
POST   /docs  { writes: [{ p, data, method? }] } → { status: 'committed', written, results: [{ p, seq? }] }
GET    /install?name=<ns> · POST /install { manifest, approving? } · DELETE /install?name=
```

Every request carries `Authorization: Bearer tsp_…`, a scoped capability
token from the [authorize](/authorization/) loop. The token is the writer's
identity: it never appears in a URL, a log line, or an error message.

## `RemoteLog`

`RemoteLog` implements `SyncLog` over one installed collection, `virta:event`
by default (see the [manifest](/manifest/)), which the manifest declares
with `envelope: { seq: true }`. A **push** is one atomic commit —
`POST /docs` with every event as an upsert keyed by its own id, so a
retried push is a no-op and a torn commit cannot happen — and the host's
`_seq` for each event comes back in the result. A **pull** is the delta
query, `GET /docs?since=<cursor>`, which answers `{ rows, cursor, more }`;
`more` is the host's word, never inferred from a short page. A host that
has not sequenced the collection answers `not-sequenced`, which surfaces as
a clear error rather than an empty log.

This is service-compris 0.2.0-beta.2's surface (tosijs-platform#14 and
#15, both filed from here and both closed within a day). The cost the
platform is explicit about: a sequenced collection serialises its writes,
roughly one commit a second. An event log with a few writers is inside
that; a bulk import is one commit of up to a hundred events at a time.

Verified end to end against virta's own host (`virta-7e070`, 2026-09-21):
push, delta pull, a second store replaying to the same state.
*/

/*{ "parent": "Reference", "order": 95 }*/

import type { EventDraft, VirtaEvent } from './model'
import { DEFAULT_PAGE, type LogPage, type SyncLog } from './log'

/** Just the call: Bun's `typeof fetch` carries extras (`preconnect`) a wrapper need not. */
export type Fetch = (
  input: string | URL | Request,
  init?: RequestInit
) => Promise<Response>

export interface ServiceOptions {
  /** `https://<region>-<project>.cloudfunctions.net`, or an emulator URL. */
  host: string
  /**
   * The bearer: a scoped capability token (`tsp_…`), or a function that
   * returns the current one — a Firebase ID token refreshes, so a signed-in
   * board passes `() => user.getIdToken()`. Absent means anonymous.
   */
  token?: string | (() => Promise<string>)
  /** Injected for tests and for runtimes without a global fetch. */
  fetch?: Fetch
}

/**
 * A body that is a web page rather than the host's JSON: a site's address
 * given where the service's belongs (on Firebase the site and the API live
 * at different origins), or a proxy's error page.
 */
export function isWebPage(contentType: string | null, text: string): boolean {
  return (
    /\btext\/html\b/i.test(contentType ?? '') ||
    /^\s*<(!doctype html|html)[\s>]/i.test(text)
  )
}

/** The one-line diagnosis for a web page where the host's JSON belongs — never the page itself. */
export function webPageDiagnosis(status: number): string {
  return status >= 500
    ? 'the host answered with an HTML error page (a proxy or gateway failure?)'
    : 'this address serves a website, not a service-compris endpoint — its API may live elsewhere (on Firebase: the cloudfunctions URL)'
}

/**
 * A non-2xx answer, or a web page where JSON belongs (`code` \`not-a-service\`
 * then, unless it was a 5xx error page). `code` is the host's stable error code (`forbidden`,
 * `not-found`, `schema`, `refused`, `not-sequenced`, …) when the body is
 * the platform's `{ error, message, details? }` shape, else `''`; `body`
 * is the raw text. Switch on the code; the prose may change.
 */
export class ServiceError extends Error {
  readonly code: string
  readonly details: unknown
  constructor(
    public readonly status: number,
    public readonly method: string,
    public readonly route: string,
    public readonly body: string,
    /** the answer's content type, when known: a page can say so without a doctype */
    contentType: string | null = null
  ) {
    let code = ''
    let details: unknown
    let message = body
    const webPage = isWebPage(contentType, body)
    if (webPage) {
      code = status >= 500 ? '' : 'not-a-service'
      message = webPageDiagnosis(status)
    } else
      try {
        const j = JSON.parse(body) as {
          error?: unknown
          message?: unknown
          details?: unknown
        }
        if (typeof j.error === 'string') code = j.error
        if (typeof j.message === 'string') message = j.message
        details = j.details
      } catch {
        /* plain text body */
      }
    // a refusal's problems are the useful part; put the first few in the message
    const problems =
      details && typeof details === 'object' && 'problems' in details
        ? (details as { problems?: unknown }).problems
        : undefined
    const detail = Array.isArray(problems)
      ? ` [${problems.slice(0, 3).map(String).join('; ')}]`
      : ''
    super(
      `${method} ${route} → ${status}${code ? ` ${code}` : ''}${
        message ? `: ${message.slice(0, 200)}` : ''
      }${detail}`
    )
    this.name = 'ServiceError'
    this.code = code
    this.details = details
  }
}

/** The host's 404 — "not there, or not yours" — and never a website's: that is a wrong address. */
const isMissing = (e: unknown): boolean =>
  e instanceof ServiceError && e.status === 404 && e.code !== 'not-a-service'

export interface ServiceResponse {
  status: number
  text: string
  json: unknown
}

export type WriteOutcome = 'created' | 'updated' | 'unchanged' | 'exists'

export interface ListOptions {
  /** how many records (`c`); the host caps this */
  count?: number
  /** `field` or `field(desc)` (`o`) */
  order?: string
  /** projection (`f`) */
  fields?: string[]
}

/**
 * The client. Methods map one to one onto routes; nothing here knows what
 * a task is. Errors are `ServiceError`s carrying the status and the short
 * body the endpoint sends (`forbidden`, `not found`, a schema problem).
 */
/** What `PUT /blob` answers: the file as the host measured it. */
export interface BlobStored {
  status: 'stored' | 'replaced' | 'unchanged'
  path: string
  bytes: number
  sha256: string
}

export class ServiceClient {
  readonly host: string
  private readonly token: string | (() => Promise<string>) | undefined
  private readonly fetchImpl: Fetch

  constructor(options: ServiceOptions) {
    this.host = options.host.replace(/\/$/, '')
    this.token = options.token
    const f = options.fetch ?? (globalThis.fetch as Fetch | undefined)
    if (!f) throw new Error('virta: no fetch available; pass one in')
    // Never call the browser's fetch as a method of this object: Chrome
    // answers "Illegal invocation" (Bun and Node do not care, which is how
    // this reached a browser). Wrap it so `this` is never the client.
    this.fetchImpl = (input, init) => f(input, init)
  }

  /** Injectable for tests: how a 429's `Retry-After` is waited out. */
  sleep: (ms: number) => Promise<void> = (ms) =>
    new Promise((r) => setTimeout(r, ms))
  /** How many times a rate-limited request is retried before it fails. */
  retries = 3

  /**
   * One request. Query values are encoded; a body is JSON. The host rate
   * limits at 100 requests a minute per address and answers `429` with
   * `Retry-After`; that is waited out and retried, so a bulk push paces
   * itself instead of failing half way.
   */
  async request(
    method: string,
    route: string,
    options: {
      query?: Record<string, string | number | undefined>
      body?: unknown
    } = {}
  ): Promise<ServiceResponse> {
    const url = new URL(this.host + route)
    for (const [k, v] of Object.entries(options.query ?? {})) {
      if (v !== undefined && v !== '') url.searchParams.set(k, String(v))
    }
    const headers: Record<string, string> = {}
    if (this.token) {
      const bearer =
        typeof this.token === 'function' ? await this.token() : this.token
      if (bearer) headers.Authorization = `Bearer ${bearer}`
    }
    if (options.body !== undefined) headers['Content-Type'] = 'application/json'
    let res: Response
    let attempt = 0
    for (;;) {
      res = await this.fetchImpl(url.toString(), {
        method,
        headers,
        body:
          options.body === undefined ? undefined : JSON.stringify(options.body),
      })
      if (res.status !== 429 || attempt++ >= this.retries) break
      // a host can say any number; nobody waits more than a minute on its word
      const after = Number(res.headers.get('Retry-After'))
      const seconds =
        Number.isFinite(after) && after > 0 ? Math.min(after, 60) : 5
      await this.sleep(seconds * 1000)
    }
    const text = await res.text()
    // a web page is never an answer, whatever its status: a 200 from a
    // site's address must not reach a caller as if it were data
    const contentType = res.headers.get('content-type')
    if (isWebPage(contentType, text))
      throw new ServiceError(res.status, method, route, text, contentType)
    let json: unknown = null
    try {
      json = text ? JSON.parse(text) : null
    } catch {
      json = null
    }
    if (res.status < 200 || res.status >= 300) {
      throw new ServiceError(res.status, method, route, text)
    }
    return { status: res.status, text, json }
  }

  /** One request with bytes, not JSON: the file routes (`/blob`). 429s are waited out like any. */
  private async send(
    method: string,
    route: string,
    init: { body?: BodyInit; contentType?: string } = {}
  ): Promise<Response> {
    const headers: Record<string, string> = {}
    if (this.token) {
      const bearer =
        typeof this.token === 'function' ? await this.token() : this.token
      if (bearer) headers.Authorization = `Bearer ${bearer}`
    }
    if (init.contentType) headers['Content-Type'] = init.contentType
    let res: Response
    let attempt = 0
    for (;;) {
      res = await this.fetchImpl(this.host + route, {
        method,
        headers,
        body: init.body,
      })
      if (res.status !== 429 || attempt++ >= this.retries) break
      const after = Number(res.headers.get('Retry-After'))
      await this.sleep(
        (Number.isFinite(after) && after > 0 ? Math.min(after, 60) : 5) * 1000
      )
    }
    return res
  }

  /**
   * `PUT /blob/<area>/<path>`: store a file (service-compris 0.3.0). The host
   * measures its size and hash. Identical bytes again are `unchanged`. The
   * host's refusals are opaque: a 404 means the area is unknown to this
   * credential — for a CLI token, most often one scoped to `virta:event`
   * only — so the error says how to get past it.
   */
  async putBlob(
    area: string,
    path: string,
    bytes: BodyInit,
    contentType: string
  ): Promise<BlobStored> {
    const route = `/blob/${area}/${path}`
    const res = await this.send('PUT', route, { body: bytes, contentType })
    const text = await res.text()
    if (res.status === 404)
      throw new ServiceError(
        404,
        'PUT',
        route,
        `${text}\n(this credential can't reach ${area}: a token scoped to virta:event only can't store files — run virta self login for one that can; from an agent's shell it polls, so the approver can be on any device)`
      )
    if (res.status < 200 || res.status >= 300)
      throw new ServiceError(res.status, 'PUT', route, text)
    return JSON.parse(text) as BlobStored
  }

  /** `DELETE /blob/<area>/<path>`: remove a stored file; one already gone is not an error. */
  async deleteBlob(area: string, path: string): Promise<void> {
    const route = `/blob/${area}/${path}`
    const res = await this.send('DELETE', route)
    if (res.status === 404) return
    if (res.status < 200 || res.status >= 300)
      throw new ServiceError(res.status, 'DELETE', route, await res.text())
  }

  /**
   * `GET /blob/<area>/<path>`: the file, following a signed-URL redirect or
   * streamed by the function. `null` when it's missing or not this
   * credential's to read (the host says the same for both).
   */
  async getBlob(area: string, path: string): Promise<Blob | null> {
    const route = `/blob/${area}/${path}`
    const res = await this.send('GET', route)
    if (res.status === 404) return null
    if (res.status < 200 || res.status >= 300)
      throw new ServiceError(res.status, 'GET', route, await res.text())
    return res.blob()
  }

  /**
   * `GET /doc`; a missing document is `null`, not an error. Note the host's
   * denials are opaque: a document the token may not read is also a 404, so
   * `null` means "not there, or not yours". Check the caveats before the data.
   */
  async getDoc(path: string): Promise<Record<string, unknown> | null> {
    try {
      const res = await this.request('GET', '/doc', { query: { p: path } })
      return res.json as Record<string, unknown>
    } catch (e) {
      if (isMissing(e)) return null
      throw e
    }
  }

  /** `PUT /doc`: update only — a missing path is refused; an identical body is `unchanged`. */
  async putDoc(
    path: string,
    data: Record<string, unknown>
  ): Promise<WriteOutcome> {
    const res = await this.request('PUT', '/doc', { body: { p: path, data } })
    return outcomeOf(res.text)
  }

  /**
   * `POST /doc`: create only. An existing path is `exists`, not an error —
   * the endpoint answers `403 document already exists` once the caller has
   * proved write access, so it is not an opaque denial.
   */
  async postDoc(
    path: string,
    data: Record<string, unknown>
  ): Promise<WriteOutcome> {
    try {
      const res = await this.request('POST', '/doc', {
        body: { p: path, data },
      })
      return outcomeOf(res.text)
    } catch (e) {
      if (e instanceof ServiceError && e.status === 403 && e.code === 'exists')
        return 'exists'
      throw e
    }
  }

  async deleteDoc(path: string): Promise<void> {
    await this.request('DELETE', '/doc', { query: { p: path } })
  }

  /** `GET /docs`: a page of records, each carrying its `_path`. */
  async listDocs(
    collection: string,
    options: ListOptions = {}
  ): Promise<{ records: Record<string, unknown>[] }> {
    // the single-document escape hatch: a plain page of records, no cursor.
    // A log reads through `listDelta`, never this.
    const res = await this.request('GET', '/docs', {
      query: {
        p: collection,
        c: options.count,
        o: options.order,
        f: options.fields?.join(','),
      },
    })
    return {
      records: Array.isArray(res.json)
        ? (res.json as Record<string, unknown>[])
        : [],
    }
  }

  /**
   * `GET /docs?since=`: the delta of a sequenced collection — every row with
   * `_seq > since` in order, the cursor to resume from, and whether more
   * remain. A collection without `envelope.seq` answers `not-sequenced`.
   */
  async listDelta(
    collection: string,
    since: number,
    count?: number
  ): Promise<{
    rows: Record<string, unknown>[]
    cursor: number
    more: boolean
  }> {
    const res = await this.request('GET', '/docs', {
      query: { p: collection, since, c: count },
    })
    const page = res.json as {
      rows?: unknown
      cursor?: unknown
      more?: unknown
    } | null
    return {
      rows: Array.isArray(page?.rows)
        ? (page!.rows as Record<string, unknown>[])
        : [],
      cursor: typeof page?.cursor === 'number' ? page.cursor : since,
      more: page?.more === true,
    }
  }

  /**
   * `POST /docs`: several documents in one transaction, all or nothing. A
   * write without `method` is an upsert (create, replace, or no-op if
   * identical); `POST` / `PUT` keep the strict guards. At most 100.
   */
  async commit(
    writes: {
      p: string
      data: Record<string, unknown>
      method?: 'POST' | 'PUT'
    }[]
  ): Promise<CommitResult> {
    const res = await this.request('POST', '/docs', { body: { writes } })
    return res.json as CommitResult
  }

  /**
   * `GET /install?name=`: is this one library installed, and at which
   * version — answerable by any authenticated principal, a token included.
   * `null` when nothing is installed under that name.
   */
  async installedAs(
    name: string
  ): Promise<{ name: string; version: string; status: string } | null> {
    try {
      const res = await this.request('GET', '/install', { query: { name } })
      return res.json as { name: string; version: string; status: string }
    } catch (e) {
      if (isMissing(e)) return null
      throw e
    }
  }

  /** `GET /install`: everything installed (configurator only). */
  async installed(): Promise<unknown> {
    return (await this.request('GET', '/install')).json
  }

  /**
   * `POST /install`. 200 installed/upgraded, 202 needs-approval (re-post with
   * `approving` set to exactly the declarations it lists), 400 refused, 409
   * conflict — the last two arrive as `ServiceError`s.
   */
  async install(
    manifest: unknown,
    approving?: unknown
  ): Promise<InstallOutcome> {
    const res = await this.request('POST', '/install', {
      body: approving === undefined ? { manifest } : { manifest, approving },
    })
    return res.json as InstallOutcome
  }
}

export interface CommitResult {
  status: 'committed'
  written: number
  results: { p: string; seq?: number }[]
}

/** The most documents one `POST /docs` commit may carry. */
export const COMMIT_LIMIT = 100

export type InstallOutcome =
  | {
      status: 'installed' | 'upgraded'
      name: string
      version: string
      unenforced: string[]
    }
  | {
      status: 'needs-approval'
      added: unknown[]
      unenforced: string[]
      note: string
    }

function outcomeOf(text: string): WriteOutcome {
  if (text.startsWith('created')) return 'created'
  if (text.startsWith('unchanged')) return 'unchanged'
  return 'updated'
}

export interface RemoteLogOptions {
  client: ServiceClient
  /** the installed collection holding events; the manifest's default */
  collection?: string
}

export const EVENT_COLLECTION = 'virta:event'

/**
 * `SyncLog` over an installed collection. Events are documents keyed by
 * their own id, so a push is idempotent by construction. See the module
 * note for what the pull half is waiting on.
 */
export class RemoteLog implements SyncLog {
  private readonly client: ServiceClient
  readonly collection: string

  constructor(options: RemoteLogOptions) {
    this.client = options.client
    this.collection = options.collection ?? EVENT_COLLECTION
  }

  async pull(after: number, limit = DEFAULT_PAGE): Promise<LogPage> {
    let page
    try {
      page = await this.client.listDelta(this.collection, after, limit)
    } catch (e) {
      if (e instanceof ServiceError && e.code === 'not-sequenced')
        throw new Error(
          `virta: ${this.collection} is not sequenced on this host — the manifest must declare envelope: { seq: true } (${e.message})`
        )
      throw e
    }
    const events = page.rows.map((r) => recordToEvent(r, this.collection))
    for (const e of events) {
      // the host orders by _seq; anything else is a host bug worth a loud stop
      if (!Number.isInteger(e.seq) || e.seq <= after)
        throw new Error(
          `virta: ${this.collection} answered a delta row with seq ${String(
            e.seq
          )} after ${after}`
        )
    }
    return { events, cursor: page.cursor, more: page.more }
  }

  /**
   * One atomic commit per hundred drafts, each an upsert keyed by the
   * event's own id: a retried push is a no-op (the host answers `written:
   * 0` and keeps the documents' sequence numbers), and a torn commit cannot
   * happen. The host's `_seq` for each event is returned, so a caller can
   * cite it — though the store still builds state only from a replay.
   */
  async push(drafts: EventDraft[]): Promise<VirtaEvent[]> {
    const out: VirtaEvent[] = []
    for (let i = 0; i < drafts.length; i += COMMIT_LIMIT) {
      const chunk = drafts.slice(i, i + COMMIT_LIMIT)
      const result = await this.client.commit(
        chunk.map((d) => ({
          p: eventPath(this.collection, d.id),
          data: toDocument(d),
        }))
      )
      const seqs = new Map(result.results.map((r) => [r.p, r.seq ?? 0]))
      for (const d of chunk) {
        const path = eventPath(this.collection, d.id)
        let seq = seqs.get(path)
        if (seq === undefined) {
          // a no-op: the host already held an identical document and left
          // it alone (`written` omits it). Its seq is the stored one — read
          // it, so a retried push returns the same events as the first.
          const doc = await this.client.getDoc(path)
          seq = typeof doc?._seq === 'number' ? doc._seq : 0
        }
        out.push({ ...d, seq })
      }
    }
    return out
  }
}

/**
 * The document path for an event. Ids are minted by writers and may carry
 * `/` (`gh:owner/repo#12:created`), which the host would read as a
 * sub-collection, so the id is percent-encoded in the path. The document's
 * own `id` field stays plain, and that is what a read uses.
 */
function eventPath(collection: string, id: string): string {
  return `${collection}/${encodeURIComponent(id)}`
}

/** The document for an event: its own fields only — the host stamps the rest. */
function toDocument(d: EventDraft): Record<string, unknown> {
  const { attribution, ...own } = d as EventDraft & { attribution?: unknown }
  void attribution
  return own as Record<string, unknown>
}

function recordToEvent(
  r: Record<string, unknown>,
  collection: string
): VirtaEvent {
  // the host's stamps all start with `_` (`_path`, `_created`, `_modified`,
  // `_seq`, `_by`, `_id`, `_collection`, …); an event's own fields never do
  const rest: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(r)) if (!k.startsWith('_')) rest[k] = v
  const path = typeof r._path === 'string' ? r._path : ''
  const id =
    typeof rest.id === 'string'
      ? rest.id
      : path
      ? decodeURIComponent(path.slice(collection.length + 1))
      : ''
  const event = { ...rest, id, seq: Number(r._seq) } as unknown as VirtaEvent
  if (r._by && typeof r._by === 'object')
    event.attribution = r._by as VirtaEvent['attribution']
  return event
}

/** virta's own board: where a link with no host of its own points. */
export const DEFAULT_BOARD = 'https://virta.tosijs.net/host/'

/**
 * The board a host serves, for links people open: `${origin}/host/`, and
 * virta's functions host fronts virta.tosijs.net (one rule for login, the
 * brief and a skill's approval link; review 2026-10-16).
 */
export function boardUrlFor(host: string): string {
  const url = new URL(host)
  if (
    /virta-7e070\.cloudfunctions\.net$/.test(url.host) ||
    url.host === 'virta.tosijs.net'
  )
    return DEFAULT_BOARD
  return `${url.origin}/host/`
}
